import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useThemeStore } from './store/theme-store';
import { Toast } from './components/ui/Toast';
import ErrorBoundary from './components/ui/ErrorBoundary';
import AnimatedLayout from './components/AnimatedLayout';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import AuthPage from './pages/AuthPage';
import SettingsPage from './pages/SettingsPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import NotFoundPage from './pages/NotFoundPage';
import ForbiddenPage from './pages/ForbiddenPage';
import PricingPage from './pages/PricingPage';
import AdminDashboard from './pages/AdminDashboard';
import GiftPage from './pages/GiftPage';
import GiftsPage from './pages/GiftsPage';
import RedeemPage from './pages/RedeemPage';
import NotePage from './pages/NotePage';
import { useAuthStore } from './store/auth-store';

const AnimatedRoutes = () => {
  const location = useLocation();
  const { user } = useAuthStore();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route 
          path="/" 
          element={
            <AnimatedLayout>
              {user ? <Navigate to="/dashboard" /> : <HomePage />}
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <AnimatedLayout>
              <DashboardPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <AnimatedLayout>
              <SettingsPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/admin" 
          element={
            <AnimatedLayout>
              <AdminDashboard />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/auth" 
          element={
            <AnimatedLayout>
              {user ? <Navigate to="/dashboard" /> : <AuthPage />}
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/login" 
          element={
            <AnimatedLayout>
              {user ? <Navigate to="/dashboard" /> : <AuthPage defaultMode="login" />}
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/register" 
          element={
            <AnimatedLayout>
              {user ? <Navigate to="/dashboard" /> : <AuthPage defaultMode="register" />}
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            <AnimatedLayout>
              {user ? <Navigate to="/dashboard" /> : <ResetPasswordPage />}
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/privacy" 
          element={
            <AnimatedLayout>
              <PrivacyPolicy />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/terms" 
          element={
            <AnimatedLayout>
              <TermsOfService />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/pricing" 
          element={
            <AnimatedLayout>
              <PricingPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/pricing/codenest-pro" 
          element={
            <AnimatedLayout>
              <PricingPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/pricing/codenest-premium" 
          element={
            <AnimatedLayout>
              <PricingPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/gift/codenest-pro" 
          element={
            <AnimatedLayout>
              <GiftPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/gift/codenest-premium" 
          element={
            <AnimatedLayout>
              <GiftPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/gifts" 
          element={
            <AnimatedLayout>
              <GiftsPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/redeem" 
          element={
            <AnimatedLayout>
              <RedeemPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/notes/:id" 
          element={
            <AnimatedLayout>
              <NotePage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/403" 
          element={
            <AnimatedLayout>
              <ForbiddenPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="/404" 
          element={
            <AnimatedLayout>
              <NotFoundPage />
            </AnimatedLayout>
          } 
        />
        <Route 
          path="*" 
          element={
            <AnimatedLayout>
              <Navigate to="/404" />
            </AnimatedLayout>
          } 
        />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  const theme = useThemeStore((state) => state.theme);
  
  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);

  return (
    <ErrorBoundary>
      <div className={theme}>
        <div className="min-h-screen bg-background text-foreground">
          <Router>
            <AnimatedRoutes />
            <Toast />
          </Router>
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;